/**
 * Cloudburst Report Handler
 * Handles: Daily Report (PDF)
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import govtLogo from '../Govt_logo.png';

const loadLogo = (src) =>
  new Promise((resolve, reject) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.src = src;
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = reject;
  });

const formatDateToDDMMYYYY = (dateStr) => {
  if (!dateStr) return '';
  if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = dateStr.split('-');
    return `${day}/${month}/${year}`;
  }
  if (dateStr.match(/^\d{2}-\d{2}-\d{4}$/)) return dateStr.replace(/-/g, '/');
  if (dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) return dateStr;
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

const generateISTFilename = (prefix) => {
  const now = new Date();
  const istTime = new Date(now.getTime() + (5.5 * 60 * 60 * 1000));
  const istDate = istTime.toISOString().split('T')[0];
  const istTimeStr = istTime.toISOString().split('T')[1].split('.')[0].replace(/:/g, '');
  return `${prefix}_${istDate}_${istTimeStr}`;
};

const getSafeNumericValue = (value) => (value !== null && value !== undefined) ? String(value) : '0';
const getSafeStringValue = (value) => (value === null || value === undefined || String(value).trim() === '') ? 'NA' : String(value).trim();

export const handleCloudburstDailyReportPDF = async (message, format, rawData) => {
  if (format !== 'pdf') return alert('Cloudburst Daily Report only supports PDF format');

  try {
    const doc = new jsPDF('l', 'pt', 'a4');
    const reportDate = message.debug?.tool_result?.parameters?.date || message.debug?.parameters?.date || new Date().toISOString().split('T')[0];
    const displayDate = formatDateToDDMMYYYY(reportDate);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const centerX = pageWidth / 2;

    let logoImg = null;
    try { logoImg = await loadLogo(govtLogo); } catch (e) {}
    if (logoImg) doc.addImage(logoImg, 'JPEG', centerX - 30, 20, 60, 60);

    doc.setFont('Helvetica', 'normal');
    doc.setFontSize(13);
    doc.text('Govt. of ABC State', centerX, 95, { align: 'center' });
    doc.text('Department Name', centerX, 115, { align: 'center' });
    doc.text(`Cloud Burst Report (Dated on ${displayDate})`, centerX, 135, { align: 'center' });

    let rows = [];
    const apiData = rawData?.dr_cloud_burst || rawData?.heatwave || rawData?.cloudburst || rawData?.dr_cloudburst;
    const apiCummData = rawData?.dr_cloud_burst_cumm || rawData?.heatwave_cumm || rawData?.dr_cloudburst_Cumm;

    if (apiData && Array.isArray(apiData)) {
      rows = apiData;
      if (apiCummData && Array.isArray(apiCummData)) {
        const cummMap = {};
        apiCummData.forEach(cummRow => { cummMap[cummRow.region_id] = cummRow; });
        rows = rows.map(row => {
          const cummData = cummMap[row.region_id];
          if (cummData) {
            return {
              ...row,
              no_of_deaths_cum: cummData.no_of_deaths_cum,
              no_of_dec_ex_gratia_pay_done_cum: cummData.no_of_dec_ex_gratia_pay_done_cum,
              no_of_persons_hospitalized_cum: cummData.no_of_persons_hospitalized_cum,
              no_of_persons_hospitalized_pay_done_cum: cummData.no_of_persons_hospitalized_pay_done_cum
            };
          }
          return row;
        });
      }
    } else if (Array.isArray(rawData)) {
      rows = rawData;
    }

    let districtName = '[DISTRICT]';
    if (rawData?.district_name) districtName = String(rawData.district_name).trim().toUpperCase();
    else if (rows?.length > 0 && rows[0]?.district_name) districtName = String(rows[0].district_name).trim().toUpperCase();

    // MATCHES EXACT WEB UI PDF STRINGS
    const tableHeaders = [
      'S.No.',
      'Block Name',
      'Number of \n Deaths due \n to Cloud \n Burst (Today)',
      'Number of \n Deaths due \n to Cloud \n Burst \n (Cumulative)',
      'Number of \n deceased \n person for \n whom \n ex-gratia \n payment \n done (Today)',
      'Number of \n deceased \n person for \n whom \n ex-gratia \n payment \n done \n (Cumulative)',
      'Number of \n persons \n hospitalized \n (Today)',
      'Number of \n persons \n hospitalized \n (Cumulative)',
      'Number of \n hospitalized \n person for \n whom \n payment \n done (Today)',
      'Number of \n hospitalized \n person for \n whom \n payment \n done \n (Cumulative)',
      'Remarks'
    ];

    const filteredRows = rows.filter(row => row && typeof row === 'object' && (row.block_name || row.no_of_deaths_today || row.remarks));

    if (filteredRows.length === 0) return alert('No block data found in report!');

    const tableData = filteredRows.map((row, idx) => [
      String(idx + 1),
      getSafeStringValue(row.block_name),
      getSafeNumericValue(row.no_of_deaths_today),
      getSafeNumericValue(row.no_of_deaths_cum),
      getSafeNumericValue(row.no_of_dec_ex_gratia_pay_done_today),
      getSafeNumericValue(row.no_of_dec_ex_gratia_pay_done_cum),
      getSafeNumericValue(row.no_of_persons_hospitalized_today),
      getSafeNumericValue(row.no_of_persons_hospitalized_cum),
      getSafeNumericValue(row.no_of_persons_hospitalized_pay_done_today),
      getSafeNumericValue(row.no_of_persons_hospitalized_pay_done_cum),
      getSafeStringValue(row.remarks)
    ]);

    autoTable(doc, {
      head: [tableHeaders],
      body: tableData,
      startY: 160,
      theme: 'grid',
      bodyStyles: { lineColor: [0, 0, 0], lineWidth: 0.1, font: 'Helvetica', fontSize: 8 },
      headStyles: { fillColor: [255, 255, 255], textColor: [0, 0, 0], lineWidth: 0.1, halign: 'center', font: 'Helvetica', fontSize: 8 },
      styles: { cellPadding: 3, halign: 'left', overflow: 'linebreak' }
    });

    const footerY = pageHeight - 120;
    doc.setFontSize(10);
    doc.text('Letter No.:', 50, footerY);
    doc.text('Date:', 50, footerY + 20);
    doc.text('Copy:', 50, footerY + 40);
    doc.text('Signature of District Officers/', 500, footerY);
    doc.text('Additional District Magistrate (ADM) (Disaster Management)/', 500, footerY + 15);
    doc.text('Officer in Charge (District Disaster Management)/', 500, footerY + 30);
    doc.setFont('Helvetica', 'bold');
    doc.text(`[${districtName} DISTRICT]`, 500, footerY + 50);

    doc.save(generateISTFilename('daily_cloud_burst_report') + '.pdf');

  } catch (error) {
    console.error('❌ Cloudburst PDF Error:', error);
    alert(`PDF Generation Error: ${error.message}`);
  }
};

export const isCloudburstReport = (toolName) => ['cloudburst_daily_report', 'cloudburstdailyreport'].includes(toolName?.toLowerCase());
export const isCloudburstStatus = (toolName) => ['cloudburst_daily_report_status', 'cloudburstdailyreportstatus'].includes(toolName?.toLowerCase());
export const isCloudburstMIS = (toolName) => ['cloudburst_mis_district', 'cloudburst_mis_district_datewise', 'cloudburst_mis_state', 'cloudburst_mis_state_datewise'].includes(toolName?.toLowerCase());